<!--
  LifecycleStepper.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Four-stage stepper showing where the artwork sits in its lifecycle:
  Uploaded -> Digitizing -> Awaiting approval -> Decision. The fourth
  step renders one of three terminal labels depending on the current
  status:

    - approved          -> "Approved"
    - changes_requested -> "Changes requested"
    - rejected          -> "Rejected"
    - everything else   -> "Decision"

  Status -> stage mapping comes from ADR 0035's pipeline_state model:

    uploaded            -> stage 0 (Uploaded)
    digitizing          -> stage 1 (Digitizing)
    awaiting_approval   -> stage 2 (Awaiting approval)
    changes_requested   -> stage 3 (Decision, terminal label)
    rejected            -> stage 3 (Decision, terminal label)
    approved            -> stage 3 (Decision, terminal label)

  Props:
    - status: string -- ApprovalRecord.status OR Artwork.pipeline_state.
      Either is accepted; the four pipeline-only states (uploaded /
      digitizing) come from the Artwork, the four record-status states
      from the ApprovalRecord.
-->
<template>
    <ol class="aw-stepper" data-testid="lifecycle-stepper">
        <li
            v-for="(step, idx) in steps"
            :key="step.key"
            class="aw-stepper__step"
            :class="stepClass(idx, step)"
            :data-testid="`lifecycle-stepper-step-${step.key}`"
        >
            <span class="aw-stepper__dot">{{ idx + 1 }}</span>
            <span class="aw-stepper__label">{{ step.label }}</span>
            <span
                v-if="idx < steps.length - 1"
                class="aw-stepper__bar"
                :class="{
                    'aw-stepper__bar--done': idx < currentStage,
                }"
            ></span>
        </li>
    </ol>
</template>

<script>
const STAGE_FOR_STATUS = {
    uploaded: 0,
    digitizing: 1,
    awaiting_approval: 2,
    changes_requested: 3,
    rejected: 3,
    approved: 3,
}

export default {
    name: 'LifecycleStepper',
    props: {
        status: {
            type: String,
            required: true,
        },
    },
    computed: {
        currentStage() {
            const s = STAGE_FOR_STATUS[this.status]
            return typeof s === 'number' ? s : 0
        },
        terminalLabel() {
            if (this.status === 'approved') return 'Approved'
            if (this.status === 'changes_requested') return 'Changes requested'
            if (this.status === 'rejected') return 'Rejected'
            return 'Decision'
        },
        steps() {
            return [
                { key: 'uploaded', label: 'Uploaded' },
                { key: 'digitizing', label: 'Digitizing' },
                { key: 'awaiting_approval', label: 'Awaiting approval' },
                { key: 'decision', label: this.terminalLabel },
            ]
        },
    },
    methods: {
        stepClass(idx) {
            const isDone = idx < this.currentStage
            const isCurrent = idx === this.currentStage
            const isTerminal = idx === this.steps.length - 1
            return {
                'aw-stepper__step--done': isDone,
                'aw-stepper__step--current': isCurrent,
                'aw-stepper__step--rejected':
                    isCurrent && isTerminal && this.status === 'rejected',
                'aw-stepper__step--changes':
                    isCurrent &&
                    isTerminal &&
                    this.status === 'changes_requested',
                'aw-stepper__step--approved':
                    isCurrent && isTerminal && this.status === 'approved',
            }
        },
    },
}
</script>

<style scoped>
.aw-stepper {
    list-style: none;
    padding: 0;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 0;
}
.aw-stepper__step {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--tf-fg-4, #5a6a82);
    font-size: 12px;
    position: relative;
}
.aw-stepper__dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--tf-surface-alt, #edf2f7);
    color: var(--tf-fg-4, #5a6a82);
    font-size: 11px;
    font-weight: 600;
}
.aw-stepper__label {
    white-space: nowrap;
}
.aw-stepper__bar {
    width: 28px;
    height: 2px;
    background: var(--tf-border, #e2e8f0);
    margin: 0 8px;
}
.aw-stepper__bar--done {
    background: var(--tf-primary, #1976d2);
}
.aw-stepper__step--done .aw-stepper__dot {
    background: var(--tf-primary, #1976d2);
    color: #ffffff;
}
.aw-stepper__step--current .aw-stepper__dot {
    background: var(--tf-primary-soft, rgba(25, 118, 210, 0.18));
    color: var(--tf-primary-press, #0d47a1);
    border: 1px solid var(--tf-primary, #1976d2);
}
.aw-stepper__step--current .aw-stepper__label {
    color: var(--tf-fg-1, #1a202c);
    font-weight: 500;
}
.aw-stepper__step--rejected .aw-stepper__dot {
    background: rgba(211, 47, 47, 0.16);
    border-color: #c62828;
    color: #c62828;
}
.aw-stepper__step--changes .aw-stepper__dot {
    background: #fff3df;
    border-color: #c97900;
    color: #7a4f00;
}
.aw-stepper__step--approved .aw-stepper__dot {
    background: rgba(56, 142, 60, 0.16);
    border-color: #2e7d32;
    color: #2e7d32;
}
</style>
